import { useState } from 'react';
import { MediaApi, MediaTypes } from '@frontend/api-media';
import { useTranslation } from '@frontend/i18n';
import { useMutation } from '@frontend/react-query-helpers';
import { ContentLoader, getInitials, Modal, ModalControlModalProps, useAlert } from '@frontend/design-system';
import { AvatarEditor } from '../../components/Avatar';

interface Props extends ModalControlModalProps {
  locationId: string;
  providerName: string;
  providerPhoto?: string;
  onChangeProviderPhoto: (src: string) => void;
}

export const ProviderPhotoUploadModal = ({
  locationId,
  providerName = '',
  providerPhoto = '',
  onChangeProviderPhoto,
  ...modalProps
}: Props) => {
  const alert = useAlert();
  const { t } = useTranslation('schedule');
  const [file, setFile] = useState<File>();

  const uploadMediaMutation = useMutation({
    mutationFn: (req: MediaTypes.UploadMediaPayload) => MediaApi.uploadMedia(req, locationId),
  });

  const handleChange = (file: File) => setFile(file);
  const handleDelete = () => {
    setFile(undefined);
    onChangeProviderPhoto('');

    modalProps.onClose();
  };
  const handleSetProfilePhoto = async () => {
    if (!file) {
      alert.error(t('No file selected'));
      return;
    }

    try {
      const res = await uploadMediaMutation.mutateAsync({
        data: file,
        filename: file.name,
        type: MediaTypes.MediaUploadTypes.Gallery,
      });

      setFile(undefined);
      onChangeProviderPhoto(res.PublicURL);
      modalProps.onClose();
    } catch (err) {
      alert.error(t('Failed to set profile photo'));
    }
  };

  const headerText = providerName
    ? t('Upload Profile Photo for {{name}}', { name: providerName })
    : t('Upload Profile Photo');
  return (
    <Modal {...modalProps} minWidth={400} maxWidth={600}>
      <Modal.Header>{headerText}</Modal.Header>
      <Modal.Body>
        <ContentLoader size='medium' show={uploadMediaMutation.isLoading} />
        <AvatarEditor
          initials={getInitials(providerName)}
          uploadImageLabel={t('Select Profile Photo')}
          hideRemoveButton
          alt={providerName}
          src={providerPhoto}
          onDelete={handleDelete}
          onChange={handleChange}
        />
      </Modal.Body>
      <Modal.Footer
        primary={{
          label: t('Set Profile Photo'),
          onClick: handleSetProfilePhoto,
          loading: uploadMediaMutation.isLoading,
        }}
        secondary={{
          label: t('Cancel'),
          onClick: modalProps.onClose,
          disabled: uploadMediaMutation.isLoading,
        }}
        tertiary={{
          label: t('Remove Profile Photo'),
          onClick: handleDelete,
          disabled: !providerPhoto || uploadMediaMutation.isLoading,
          destructive: true,
        }}
      />
    </Modal>
  );
};
